import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import axios from 'axios';
import { Buffer } from 'buffer';
import { BiArrowBack, BiLayout } from 'react-icons/bi';

import Topbar from '../component/Topbar';
import LoadingScreen from '../component/LoadingScreen';
import FormReportPanel from './formBuilder/FormReportPanel';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import { TemplateDto, TemplateSchema, TemplateField } from '../types/template';

export default function FormReport() {
    const { instance, accounts, inProgress } = useMsal();
    const navigate    = useNavigate();
    const [params]    = useSearchParams();
    const code        = params.get('code') ?? '';

    const [loading,   setLoading]   = useState(true);
    const [role,      setRole]      = useState<UserRole>('owner');
    const [token,     setToken]     = useState('');
    const [photoData, setPhotoData] = useState<string | null>(null);
    const [form,      setForm]      = useState<TemplateDto | null>(null);
    const [fields,    setFields]    = useState<TemplateField[]>([]);

    const api = process.env.REACT_APP_API_END_POINT;

    // ── Auth ────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        acquireGetAccessToken(instance, accounts).then(async (tokens: any) => {
            if (!tokens) return;
            try {
                const r = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${tokens.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                });
                setPhotoData('data:image/jpeg;base64,' + Buffer.from(r.data, 'binary').toString('base64'));
            } catch { /* no photo */ }
            const userRole = await checkRole(tokens.accessTokenProfile);
            saveRealRole(userRole);
            if (userRole !== 'admin' && userRole !== 'superadmin') { navigate('/accessdenied'); return; }
            setRole(getEffectiveRole(userRole));
            setToken(tokens.accessToken);
        });
    }, [inProgress, instance, accounts, navigate]);

    // ── Load form schema ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!token || !code) return;
        axios.get<TemplateDto>(`${api}/v1/Form/detail`, {
            headers: { Authorization: `Bearer ${token}` },
            params:  { code },
        })
            .then(res => {
                setForm(res.data);
                if (res.data.schemaJson) {
                    try {
                        const schema: TemplateSchema = JSON.parse(res.data.schemaJson);
                        setFields(schema.fields ?? []);
                    } catch { /* */ }
                }
            })
            .catch(() => toast.error('Failed to load form'))
            .finally(() => setLoading(false));
    }, [token, code]); // eslint-disable-line

    if (loading) return <LoadingScreen />;

    return (
        <div className="admin-layout">
            <Topbar role={role} photoData={photoData ?? undefined} token={token} />
            <main className="admin-main p-0 d-flex flex-column" style={{ height: '100vh', overflow: 'hidden' }}>
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header bar */}
                <div className="template-builder-header">
                    <Button variant="link" className="p-0 text-muted me-2" onClick={() => navigate('/forms')}>
                        <BiArrowBack size={20} />
                    </Button>
                    <BiLayout size={20} className="text-danger me-1" />
                    <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 700, fontSize: 16 }}>{form?.name ?? code}</span>
                        <span className="text-muted ms-2 small">{code}</span>
                    </div>
                </div>

                {/* Panel — same component as inside FormBuilder */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <FormReportPanel
                        token={token}
                        templateCode={code}
                        formName={form?.name ?? code}
                        fields={fields}
                    />
                </div>
            </main>
        </div>
    );
}
